/** @jsxImportSource ../simboluri-jsx */
import { useEffect, useState } from "react";
import { getFormeModale } from "../apiStructura";
import Deformata3D from "../Deformata3D";
import { FemMod, FemResult } from "../tipuri";
import Info from "../Info";
import { numar, t, useT } from "../../i18n";
import { intinderea, nr } from "./model";

// ---------------------------------------------------------------------------
// Formele modale: tab-uri per mod + deformata animată
// ---------------------------------------------------------------------------

function directieMod(m: FemMod): string {
  const v = [
    ["X", m.masa_x_pct],
    ["Y", m.masa_y_pct],
    [t("struct.fem.dir_torsiune"), m.masa_rz_pct],
  ] as const;
  const suma = m.masa_x_pct + m.masa_y_pct + m.masa_rz_pct;
  if (suma <= 0) return "—";
  const top = v.reduce((a, b) => (b[1] > a[1] ? b : a));
  return top[1] / suma >= 0.55 ? top[0] : t("struct.fem.dir_mixt");
}

export function FormeModale({ r, projectId, cloneId, versiune }: {
  r: FemResult;
  /** de unde se cer formele când rezultatul le-a lăsat deoparte
   *  (`forme_separat`); fără ele se desenează ce e în rezultat */
  projectId?: string;
  cloneId?: string | null;
  versiune?: string | null;
}) {
  useT();
  const [mod, setMod] = useState(0);
  const separate = !!r.verificare_modala?.forme_separat;
  const [aduse, setAduse] = useState<Map<number, number[][]> | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  useEffect(() => {
    if (!separate || !projectId) return;
    let viu = true;
    setAduse(null);
    setEroare(null);
    getFormeModale(projectId, cloneId, versiune)
      .then((f) => viu && setAduse(new Map(f.moduri.map((m) => [m.mod, m.forma]))))
      .catch((e) => viu && setEroare((e as Error).message));
    return () => { viu = false; };
  }, [separate, projectId, cloneId, versiune]);
  const moduri = (r.verificare_modala?.moduri ?? [])
    .map((m) => (separate && aduse ? { ...m, forma: aduse.get(m.mod) ?? m.forma } : m))
    .filter((m) => m.forma);
  if (separate && !aduse && !eroare) {
    return (
      <div className="st-findings">
        <h3>{t("struct.fem.forme")}</h3>
        <div className="sg-muted">{t("struct.fem.forme_se_aduc")}</div>
      </div>
    );
  }
  if (!moduri.length) {
    return (
      <div className="st-findings">
        <h3>{t("struct.fem.forme")}</h3>
        <div className="sg-muted">{t("struct.fem.forme_lipsa")}</div>
      </div>
    );
  }
  const m = moduri[Math.min(mod, moduri.length - 1)];

  // amplitudine vizuală: formele sunt normalizate la |u|max = 1, deci
  // scalăm la ~5% din întinderea clădirii
  const ix = intinderea(r.model.noduri.map((n) => n.x_m));
  const iy = intinderea(r.model.noduri.map((n) => n.y_m));
  const span = Math.max(ix.max - ix.min, iy.max - iy.min);
  const amp = span * 0.05;

  return (
    <div className="st-findings">
      <h3>
        {t("struct.fem.moduri")}
        <Info
          titlu={t("struct.fem.forma_titlu")}
          ce={t("struct.fem.forma_ce")}
          deCe={t("struct.fem.forma_dece")}
        />
      </h3>
      <div className="md-tabs">
        {moduri.map((mm, i) => (
          <button
            key={i}
            className={`md-tab ${i === Math.min(mod, moduri.length - 1) ? "active" : ""}`}
            onClick={() => setMod(i)}
          >
            {t("struct.fem.tab_mod", { mod: mm.mod, t: nr(mm.T_s, 2),
                                       tip: mm.tip ?? directieMod(mm) })}
          </button>
        ))}
      </div>
      <div className="sg-muted" style={{ margin: "6px 0" }}>
        {t("struct.fem.mod_sub", { t: m.T_s, f: m.f_hz, x: m.masa_x_pct,
                                   y: m.masa_y_pct, rz: m.masa_rz_pct })}
        {m.puritate != null
          && t("struct.fem.mod_puritate", { p: nr(m.puritate, 2) })}
        {m.nota_pereche ? (
          <div style={{ marginTop: 4 }}>{m.nota_pereche}</div>
        ) : null}
      </div>
      <Deformata3D
        key={m.mod}
        model={r.model}
        field={m.forma!}
        scale={amp}
        animate
        height={440}
      />
      <TabelMaseModale r={r} activ={m.mod} />
    </div>
  );
}

// Masele participante pe fiecare mod, sub vizualizare.
// Coloanele `masa_*_pct` sunt raportate la baze DIFERITE și nu sumează pe rând
// — de asta există `dist_*`, distribuția participării modului între direcții.
// Cumulatele sunt cele pe care se aplică criteriul de 90 %.
function TabelMaseModale({ r, activ }: { r: FemResult; activ: number }) {
  useT();
  const moduri = r.verificare_modala?.moduri ?? [];
  if (!moduri.length) return null;
  const masaTot = r.verificare_modala?.masa_totala_t ?? r.masa_totala_t;
  const criteriu = r.verificare_modala?.criteriu_90_ok;
  const cumX = r.verificare_modala?.masa_cumulata_x_pct;
  const cumY = r.verificare_modala?.masa_cumulata_y_pct;
  const pc = (v: number | undefined | null, zec = 1) =>
    (v == null ? "—" : `${numar(v, { minimumFractionDigits: zec,
                                     maximumFractionDigits: zec })} %`);

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <b style={{ fontSize: 13 }}>{t("struct.fem.mase_titlu")}</b>
        {masaTot != null && (
          <span className="sg-muted" style={{ fontSize: 12 }}>
            {t("struct.fem.mase_total",
               { t: numar(masaTot, { maximumFractionDigits: 0 }) })}
          </span>
        )}
        {cumX != null && cumY != null && (
          <span
            className={`ob-chip ${criteriu === false ? "ob-c-bad" : "ob-c-ok"}`}
            title={t("struct.fem.mase_criteriu")}
          >
            {t("struct.fem.mase_cumulat",
               { x: pc(cumX, 0), y: pc(cumY, 0) })}
          </span>
        )}
      </div>
      {/* Lățimea e SUMA coloanelor, nu a panoului: cu tabelul întins pe toată
          lățimea, ultima coloană («cumulat Y») primea tot spațiul rămas și
          rămânea singură la marginea dreaptă, departe de vecina ei
          . */}
      <div className="ob-card" style={{ overflow: "hidden", maxWidth: 620 }}>
        <table className="ob-sol-tabel" style={{ tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th style={{ width: 52 }}>{t("struct.fem.col_mod")}</th>
              <th style={{ width: 70, textAlign: "right" }}>T [s]</th>
              <th style={{ width: 70, textAlign: "right" }}>f [Hz]</th>
              <th style={{ width: 74, textAlign: "right" }}>
                {t("struct.fem.col_masa_x")}</th>
              <th style={{ width: 74, textAlign: "right" }}>
                {t("struct.fem.col_masa_y")}</th>
              <th style={{ width: 84, textAlign: "right" }}>
                {t("struct.fem.col_torsiune")}</th>
              <th style={{ width: 88, textAlign: "right" }}>
                {t("struct.fem.col_cum_x")}</th>
              <th style={{ width: 88, textAlign: "right" }}>
                {t("struct.fem.col_cum_y")}</th>
            </tr>
          </thead>
          <tbody>
            {moduri.map((mm) => (
              <tr
                key={mm.mod}
                style={mm.mod === activ ? { background: "var(--accbg)" } : undefined}
              >
                <td className="sg-mono">{mm.mod}</td>
                <td className="sg-mono" style={{ textAlign: "right" }}>{mm.T_s.toFixed(3)}</td>
                <td className="sg-mono" style={{ textAlign: "right" }}>{mm.f_hz.toFixed(2)}</td>
                <td className="sg-mono" style={{ textAlign: "right" }}>{pc(mm.masa_x_pct)}</td>
                <td className="sg-mono" style={{ textAlign: "right" }}>{pc(mm.masa_y_pct)}</td>
                <td className="sg-mono" style={{ textAlign: "right" }}>{pc(mm.masa_rz_pct)}</td>
                <td className="sg-mono" style={{ textAlign: "right", color: "var(--mut)" }}>
                  {pc(mm.cumulat_x_pct, 0)}
                </td>
                <td className="sg-mono" style={{ textAlign: "right", color: "var(--mut)" }}>
                  {pc(mm.cumulat_y_pct, 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sg-muted" style={{ fontSize: 12, marginTop: 6 }}>
        {t("struct.fem.coloanele_masa_baze")}
      </div>
    </div>
  );
}
